import {observer} from 'mobx-react';
import React, {useCallback} from 'react';
import {ButtonProps, ButtonStyleType} from '@udemy-v2/react-core-components';
import {TextInputProps} from '@udemy-v2/react-form-components';

import {MODES} from './constants';
import {RedeemCoupon} from './redeem-coupon.react-component';
import {useClpPurchaseContext} from '../../context/clp-purchase-context.react-component';

interface RedeemCouponContainerProps {
    className?: string;
    inputModeClassName?: string;
    is_paid?: boolean;
    enableCouponInputModeButtonProps?: ButtonProps;
    showCouponInputModeButton?: boolean;
    showDiscountsAsTextInput?: boolean;
    showTitle?: boolean;
    size?: TextInputProps['size'];
    submitButtonStyle?: ButtonStyleType;
}

/**
 * Container component that connects the RedeemCoupon UI to the coupon context
 * Handles user interactions for entering, applying, and removing coupon codes
 */
export const RedeemCouponContainer = observer((props: RedeemCouponContainerProps) => {
    const {
        className,
        inputModeClassName,
        is_paid = true,
        enableCouponInputModeButtonProps = {},
        showCouponInputModeButton = true,
        showTitle = false,
        showDiscountsAsTextInput = false,
        size,
        submitButtonStyle,
    } = props;

    // Get all coupon-related state and handlers from CLPPurchaseContext
    const {
        couponStore,
        handleCouponApply,
        handleCouponRemove,
        currentDiscountAttempts,
        couponErrorMessage,
    } = useClpPurchaseContext();

    /**
     * Handle entering input mode for coupon entry
     * Simple UI state management - no complex orchestration needed
     */
    const handleEnterInputMode = useCallback(() => {
        couponStore.enterInputMode();
    }, [couponStore]);

    /**
     * Handle input changes for coupon code entry
     * Simple UI state management - converts to uppercase and updates store
     */
    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value.toUpperCase();
            couponStore.setCode(value);
        },
        [couponStore],
    );

    if (!is_paid || couponStore.hasAlreadyPurchased || couponStore.currentMode === MODES.OFF) {
        return null;
    }

    return (
        <RedeemCoupon
            className={className}
            inputModeClassName={inputModeClassName}
            currentMode={couponStore.currentMode}
            currentDiscountAttempts={currentDiscountAttempts}
            error={couponErrorMessage}
            submittedCode={couponStore.submittedCode}
            inputCode={couponStore.inputCode}
            inputOnChange={handleInputChange}
            inputOnSubmit={handleCouponApply}
            inputIsDisabled={couponStore.currentMode === MODES.PENDING}
            onEnterInputMode={handleEnterInputMode}
            removeCouponCode={(code: string) => () => handleCouponRemove(code)}
            showCouponInputModeButton={showCouponInputModeButton}
            enableCouponInputModeButtonProps={enableCouponInputModeButtonProps}
            showDiscountsAsTextInput={showDiscountsAsTextInput}
            showTitle={showTitle}
            couponStore={couponStore}
            size={size}
            submitButtonStyle={submitButtonStyle}
        />
    );
});
